import { useState, useCallback } from "react";
import { robotsService } from "../services/robotsService";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";

/**
 * Custom hook for robot types management
 * Handles all robot type CRUD operations and state management
 */
export const useRobotTypeManagement = () => {
  const { authFetch } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  const [robotTypesData, setRobotTypesData] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    results: [],
  });
  const [loading, setLoading] = useState(false);

  /**
   * Load robot types data with pagination
   */
  const loadRobotTypes = useCallback(
    async (page = 1, perPage = 10) => {
      try {
        setLoading(true);
        const response = await robotsService.getRobotTypes(
          authFetch,
          page,
          perPage,
        );
        setRobotTypesData(response.data);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        console.error("Error loading robot types:", error);
        notifyError(`Failed to load robot types: ${errorMessage}`);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, notifyError],
  );

  /**
   * Create a new robot type
   */
  const createRobotType = useCallback(
    async (robotTypeData) => {
      try {
        const response = await robotsService.createRobotType(
          authFetch,
          robotTypeData,
        );
        notifySuccess("Robot type created successfully");
        // Reload robot types to show the new one
        await loadRobotTypes(robotTypesData.page, robotTypesData.per_page);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        console.error("Error creating robot type:", error);
        notifyError(`Failed to create robot type: ${errorMessage}`);
        throw error;
      }
    },
    [
      authFetch,
      notifySuccess,
      notifyError,
      loadRobotTypes,
      robotTypesData.page,
      robotTypesData.per_page,
    ],
  );

  /**
   * Update an existing robot type
   */
  const updateRobotType = useCallback(
    async (typeId, updateData) => {
      try {
        const response = await robotsService.updateRobotType(
          authFetch,
          typeId,
          updateData,
        );
        notifySuccess("Robot type updated successfully");
        // Reload robot types to show the updates
        await loadRobotTypes(robotTypesData.page, robotTypesData.per_page);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        console.error("Error updating robot type:", error);
        notifyError(`Failed to update robot type: ${errorMessage}`);
        throw error;
      }
    },
    [
      authFetch,
      notifySuccess,
      notifyError,
      loadRobotTypes,
      robotTypesData.page,
      robotTypesData.per_page,
    ],
  );

  /**
   * Delete a robot type
   */
  const deleteRobotType = useCallback(
    async (typeId) => {
      try {
        await robotsService.deleteRobotType(authFetch, typeId);
        notifySuccess("Robot type deleted successfully");
        // Reload robot types after deletion
        await loadRobotTypes(robotTypesData.page, robotTypesData.per_page);
        return true;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        console.error("Error deleting robot type:", error);
        notifyError(`Failed to delete robot type: ${errorMessage}`);
        return false;
      }
    },
    [
      authFetch,
      notifySuccess,
      notifyError,
      loadRobotTypes,
      robotTypesData.page,
      robotTypesData.per_page,
    ],
  );

  /**
   * Upload icon for a robot type
   */
  const uploadRobotTypeIcon = useCallback(
    async (typeId, iconFile) => {
      try {
        const response = await robotsService.uploadRobotTypeIcon(
          authFetch,
          typeId,
          iconFile,
        );
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        console.error("Error uploading robot type icon:", error);
        notifyError(`Failed to upload icon: ${errorMessage}`);
        throw error;
      }
    },
    [authFetch, notifyError],
  );

  return {
    // State
    robotTypesData,
    loading,

    // Methods
    loadRobotTypes,
    createRobotType,
    updateRobotType,
    deleteRobotType,
    uploadRobotTypeIcon,

    // Pagination helpers
    pagination: {
      current: robotTypesData.page,
      pageSize: robotTypesData.per_page,
      total: robotTypesData.total,
      onChange: (page, pageSize) => loadRobotTypes(page, pageSize),
    },
  };
};
